import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import {
  createWorkSchedule,
  deleteWorkSchedule,
  fetchWorkSchedules,
  updateWorkSchedule,
} from "../../../api/workSchedules";
import { toast } from "react-hot-toast";

const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const defaultWeekdays = () => ({
  monday: 7.5,
  tuesday: 7.5,
  wednesday: 7.5,
  thursday: 7.5,
  friday: 7.5,
  saturday: 0,
  sunday: 0,
});

const normalizeWeekdays = (raw) => {
  const base = defaultWeekdays();
  if (!raw || typeof raw !== "object") return base;
  DAY_ORDER.forEach((k) => {
    const v = raw[k];
    if (v !== undefined && v !== null && v !== "") base[k] = Number(v);
  });
  return base;
};

export const WorkSchedulesAdmin = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ schedules_count: 0, unassigned_employee_count: 0 });
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", weekdays: defaultWeekdays() });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWorkSchedules();
      setList(Array.isArray(res?.data) ? res.data : []);
      setMeta({
        schedules_count: res?.meta?.schedules_count ?? 0,
        unassigned_employee_count: res?.meta?.unassigned_employee_count ?? 0,
      });
    } catch (e) {
      toast.error(t("work_schedules_admin.load_error"));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () => {
    setEditingId("new");
    setForm({ name: "", weekdays: defaultWeekdays() });
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      weekdays: normalizeWeekdays(row.weekdays),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: "", weekdays: defaultWeekdays() });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId === "new") {
        await createWorkSchedule({ name: form.name || null, weekdays: form.weekdays });
        toast.success(t("work_schedules_admin.created"));
      } else if (editingId) {
        await updateWorkSchedule(editingId, { name: form.name || null, weekdays: form.weekdays });
        toast.success(t("work_schedules_admin.saved"));
      }
      cancelEdit();
      await load();
    } catch (e) {
      const msg = e?.response?.data?.errors;
      toast.error(Array.isArray(msg) ? msg.join(", ") : t("work_schedules_admin.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (
      !window.confirm(
        t("work_schedules_admin.confirm_delete", { name: row.displayTitle || row.name || row.id })
      )
    ) {
      return;
    }
    try {
      await deleteWorkSchedule(row.id);
      toast.success(t("work_schedules_admin.deleted"));
      if (editingId === row.id) cancelEdit();
      await load();
    } catch (e) {
      const msg = e?.response?.data?.errors;
      toast.error(Array.isArray(msg) ? msg.join(", ") : t("work_schedules_admin.delete_error"));
    }
  };

  return (
    <>
      <DashboardHeader title={t("work_schedules_admin.title")} />
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <p className="mb-4 text-sm text-slate-600">{t("work_schedules_admin.intro")}</p>
        <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <div className="font-semibold text-slate-900">{t("work_schedules_admin.summary_title")}</div>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              {t("work_schedules_admin.summary_plans", { count: meta.schedules_count })}
            </li>
            <li>
              {meta.unassigned_employee_count > 0
                ? t("work_schedules_admin.summary_unassigned_warn", {
                    count: meta.unassigned_employee_count,
                  })
                : t("work_schedules_admin.summary_unassigned_ok")}
            </li>
          </ul>
          <p className="mt-2 text-xs text-slate-600">
            {t("work_schedules_admin.assign_hint")}{" "}
            <Link to="/dashboard/users" className="font-semibold underline">
              {t("work_schedules_admin.assign_link")}
            </Link>
          </p>
        </div>

        {loading ? (
          <ResourceLoading />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={startCreate}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                <PlusIcon className="h-5 w-5" />
                {t("work_schedules_admin.new")}
              </button>
              <Link
                to="/dashboard/guide/hours#setup-status"
                className="text-sm font-semibold text-slate-700 underline decoration-slate-300 underline-offset-4"
              >
                {t("work_schedules_admin.back_guide")}
              </Link>
            </div>

            {editingId ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-4">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t("work_schedules_admin.field_name")}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder={t("work_schedules_admin.field_name_placeholder")}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {DAY_ORDER.map((day) => (
                    <div key={day}>
                      <label className="block text-xs font-medium text-slate-600">
                        {t(`work_schedules_admin.day.${day}`)}
                      </label>
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        max="24"
                        value={form.weekdays[day]}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            weekdays: { ...f.weekdays, [day]: Number(e.target.value) },
                          }))
                        }
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={save}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {saving ? t("work_schedules_admin.saving") : t("work_schedules_admin.save")}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-800"
                  >
                    {t("work_schedules_admin.cancel")}
                  </button>
                </div>
              </div>
            ) : null}

            <ul className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white shadow-sm">
              {list.length === 0 ? (
                <li className="px-4 py-8 text-center text-sm text-slate-500">
                  {t("work_schedules_admin.empty")}
                </li>
              ) : (
                list.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <div className="font-medium text-slate-900">
                        {row.displayTitle || row.name || t("work_schedules_admin.unnamed")}
                      </div>
                      <div className="text-xs text-slate-500">ID {row.id}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(row)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-800"
                      >
                        {t("work_schedules_admin.edit")}
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                        {t("work_schedules_admin.delete")}
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};
